import * as React from "react";

import { useJoinForm_asController } from "../join-form-provider";
import { useLoginForm_asController } from "../login-form-provider";
import {
  PRESENTATION_SCROLL_ACTIONS,
  usePresenterForScroll_asController,
} from "../presenter-for-scroll-provider";
import { useProminentError } from "../prominent-error-provider";

interface JoinDrawerUserExperienceConsumerInterface {
  presentingUserExperienceStep: JoinDrawerUserExperience_Step;
  handleClose: () => void;

  handleAlreadyAMemberSignIn: () => void;
  handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered: () => void;
  handleNotAMemberJoinForFree: () => void;

  handleBeginTheJoinDrawerUserExperience: () => void;
  handleBeginTheJoinDrawerUserExperience_startingFromRegister: () => void;
}
const defaultValue = {} as unknown as JoinDrawerUserExperienceConsumerInterface;
const JoinDrawerUserExperienceContext = React.createContext(defaultValue);

export enum JoinDrawerUserExperience_Step {
  NOT_PRESENTING = "NOT_PRESENTING",
  JOIN = "JOIN",
  LOGIN = "LOGIN",
}

export const JoinDrawerUserExperienceProvider: React.FunctionComponent = (
  props
) => {
  const { setProminentError } = useProminentError();
  const { resetLoginForm } = useLoginForm_asController();
  const { resetJoinForm } = useJoinForm_asController();

  const { initiateScrollAction } = usePresenterForScroll_asController();

  const [presentingUserExperienceStep, setPresentingUserExperienceStep] =
    React.useState<JoinDrawerUserExperience_Step>(
      JoinDrawerUserExperience_Step.NOT_PRESENTING
    );

  const handleBeginTheJoinDrawerUserExperience = React.useCallback(() => {
    setProminentError(null);
    resetLoginForm();
    resetJoinForm();
    setPresentingUserExperienceStep(JoinDrawerUserExperience_Step.LOGIN);
  }, [
    setProminentError,
    setPresentingUserExperienceStep,
    resetLoginForm,
    resetJoinForm,
  ]);

  const handleBeginTheJoinDrawerUserExperience_startingFromRegister =
    React.useCallback(() => {
      setProminentError(null);
      resetLoginForm();
      resetJoinForm();
      setPresentingUserExperienceStep(JoinDrawerUserExperience_Step.JOIN);
    }, [
      setProminentError,
      setPresentingUserExperienceStep,
      resetLoginForm,
      resetJoinForm,
    ]);

  const handleClose = React.useCallback(() => {
    setProminentError(null);
    setPresentingUserExperienceStep(
      JoinDrawerUserExperience_Step.NOT_PRESENTING
    );
    resetLoginForm();
    resetJoinForm();
  }, [
    setProminentError,
    resetLoginForm,
    resetJoinForm,
    setPresentingUserExperienceStep,
  ]);

  const handleAlreadyAMemberSignIn: JoinDrawerUserExperienceConsumerInterface["handleAlreadyAMemberSignIn"] =
    React.useCallback(() => {
      setProminentError(null);
      setPresentingUserExperienceStep(JoinDrawerUserExperience_Step.LOGIN);
      resetLoginForm();
      resetJoinForm();
      initiateScrollAction(PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_TOP);
    }, [
      setProminentError,
      setPresentingUserExperienceStep,
      resetLoginForm,
      resetJoinForm,
      initiateScrollAction,
    ]);

  const handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered: JoinDrawerUserExperienceConsumerInterface["handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered"] =
    handleAlreadyAMemberSignIn;

  const handleNotAMemberJoinForFree: JoinDrawerUserExperienceConsumerInterface["handleNotAMemberJoinForFree"] =
    React.useCallback(() => {
      setProminentError(null);
      setPresentingUserExperienceStep(JoinDrawerUserExperience_Step.JOIN);
      resetLoginForm();
      resetJoinForm();
      initiateScrollAction(PRESENTATION_SCROLL_ACTIONS.SCROLL_TO_TOP);
    }, [
      setProminentError,
      setPresentingUserExperienceStep,
      resetLoginForm,
      resetJoinForm,
      initiateScrollAction,
    ]);

  const value: JoinDrawerUserExperienceConsumerInterface = React.useMemo(
    () => ({
      presentingUserExperienceStep,
      handleClose,

      handleAlreadyAMemberSignIn,
      handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered,
      handleNotAMemberJoinForFree,

      handleBeginTheJoinDrawerUserExperience,
      handleBeginTheJoinDrawerUserExperience_startingFromRegister,
    }),
    [
      presentingUserExperienceStep,
      handleClose,
      handleAlreadyAMemberSignIn,
      handleUserWantsToLoginInsteadBecauseTheirEmailIsAlreadyRegistered,
      handleNotAMemberJoinForFree,
      handleBeginTheJoinDrawerUserExperience,
      handleBeginTheJoinDrawerUserExperience_startingFromRegister,
    ]
  );
  return (
    <JoinDrawerUserExperienceContext.Provider value={value}>
      {props.children || []}
    </JoinDrawerUserExperienceContext.Provider>
  );
};

export const useJoinDrawerUserExperience = () => {
  const context = React.useContext(JoinDrawerUserExperienceContext);
  if (context === defaultValue) {
    throw new Error(
      "useJoinDrawerUserExperience must be used within a JoinDrawerUserExperienceProvider"
    );
  }
  return context;
};
